import { useState, type FormEvent } from 'react';

import { contentTypesCreate, contentTypesUpdate } from '@/shared/api/generated/content/content';
import type { TypeBody } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Segmented } from '@/shared/ui/Segmented';
import { Switch } from '@/shared/ui/Switch';
import { Textarea } from '@/shared/ui/Textarea';
import { toast } from '@/shared/ui/Toast';

import { slugOf } from './keyify';
import styles from './TypeSettingsForm.module.scss';

export interface TypeSettingsFormProps {
  siteID: string;
  environment: 'production' | 'development';
  /** Заполнено — правим, пусто — заводим новую модель или компонент. */
  type?: TypeBody | undefined;
  /** Что заводим: модель (коллекция или одна запись), форму или компонент. */
  section: 'model' | 'component' | 'form';
  /** Группы компонентов, которые уже есть, — чтобы не плодить «Подборки» и «подборки». */
  groups?: string[];
  onSaved: (type: TypeBody) => void;
}

/**
 * Настройки модели или компонента — на деталке, а не во всплывающем окне
 * (решение Давида 20 октября 2026). Одна форма на создание и правку.
 */
export function TypeSettingsForm({
  siteID,
  environment,
  type,
  section,
  groups = [],
  onSaved,
}: TypeSettingsFormProps) {
  const editing = type !== undefined;

  const [name, setName] = useState(type?.name ?? '');
  const [slug, setSlug] = useState(type?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(editing);
  const [kind, setKind] = useState<TypeBody['kind']>(
    type?.kind ?? (section === 'component' ? 'block' : 'collection'),
  );
  const [description, setDescription] = useState(type?.description ?? '');
  const [group, setGroup] = useState(type?.folder ?? '');
  const [seo, setSeo] = useState(type?.seo ?? false);
  const [route, setRoute] = useState(type?.route ?? '');
  const [manualOrder, setManualOrder] = useState(type?.manual_order ?? false);
  const [readAccess, setReadAccess] = useState(type?.api_access.read ?? 'public');
  const [createAccess, setCreateAccess] = useState(type?.api_access.create ?? 'none');
  const [updateAccess, setUpdateAccess] = useState(type?.api_access.update ?? 'none');
  const [deleteAccess, setDeleteAccess] = useState(type?.api_access.delete ?? 'none');
  const [purpose, setPurpose] = useState<TypeBody['purpose']>(
    type?.purpose ?? (section === 'form' ? 'form' : 'content'),
  );
  const [error, setError] = useState<string>();
  const [slugError, setSlugError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const isModel = kind !== 'block';
  // Форма — коллекция заявок: адреса записи, ручного порядка и SEO у неё нет.
  const isForm = purpose === 'form';
  const noun = section === 'component' ? 'Компонент' : isForm ? 'Форма' : 'Модель';

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setSlugError(undefined);
    setBusy(true);

    const request = editing
      ? contentTypesUpdate(
          type.id,
          {
            name: name.trim(),
            slug,
            description,
            folder: group,
            ...(isModel && !isForm ? { seo, route: route.trim() } : {}),
            ...(kind === 'collection' && !isForm ? { manual_order: manualOrder } : {}),
            ...(kind === 'collection' && purpose !== 'users' ? { purpose } : {}),
            ...(isModel && purpose !== 'users'
              ? {
                  api_access: {
                    read: kind === 'collection' || readAccess !== 'own' ? readAccess : 'none',
                    create: kind === 'collection' ? createAccess : 'none',
                    update: kind === 'collection' ? updateAccess : 'none',
                    delete: kind === 'collection' ? deleteAccess : 'none',
                  },
                }
              : {}),
          },
          { 'If-Match': type.etag },
        )
      : contentTypesCreate(siteID, environment, {
          name: name.trim(),
          slug,
          kind,
          description,
          folder: group,
          seo: isModel && !isForm && seo,
          ...(isForm ? { purpose: 'form' as const } : {}),
        });

    request
      .then((response) => {
        toast.success(
          editing ? 'Сохранено' : `${noun} ${section === 'component' ? 'заведён' : 'заведена'}`,
        );
        onSaved(response.data as TypeBody);
      })
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.status === 409) {
          setSlugError(cause.detail);
        } else {
          setError(
            cause instanceof ApiError ? cause.detail : 'Не удалось сохранить. Попробуйте ещё раз.',
          );
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <form className={styles['form']} onSubmit={handleSubmit} noValidate>
      <div className={styles['row']}>
        <Field label="Название" required error={error}>
          <Input
            value={name}
            autoFocus={!editing}
            placeholder={section === 'component' ? 'Первый экран' : isForm ? 'Заявки' : 'Кейсы'}
            onChange={(event) => {
              setName(event.target.value);

              if (!slugTouched) {
                setSlug(slugOf(event.target.value));
              }
            }}
          />
        </Field>

        <Field
          label="Ключ в API"
          required
          error={slugError}
          hint={
            section === 'component'
              ? 'Вид секции в ответе сайту: type: «hero». Строчная латиница, цифры и дефис.'
              : isForm
                ? `Сайт отправляет заявки сюда: POST /v1/collections/${slug || 'requests'}. Строчная латиница, цифры и дефис.`
                : `Сайт запрашивает записи по нему: /v1/collections/${slug || 'cases'}. Строчная латиница, цифры и дефис.`
          }
        >
          <Input
            mono
            value={slug}
            placeholder={section === 'component' ? 'hero' : isForm ? 'requests' : 'cases'}
            onChange={(event) => {
              setSlugTouched(true);
              setSlug(event.target.value);
            }}
          />
        </Field>
      </div>

      {!editing && section === 'model' && (
        <Field label="Вид">
          <Segmented
            value={kind}
            onValueChange={(next) => {
              setKind(next as TypeBody['kind']);
            }}
            aria-label="Вид модели"
            options={[
              { value: 'collection', label: 'Много записей — кейсы, статьи' },
              { value: 'single', label: 'Одна запись — настройки, подвал' },
            ]}
          />
        </Field>
      )}

      {section === 'component' && (
        <Field
          label="Группа"
          hint="Компоненты одной группы стоят рядом: «Первые экраны», «Подборки», «Формы»."
        >
          <Input
            value={group}
            list="component-groups"
            maxLength={40}
            placeholder="Подборки"
            onChange={(event) => {
              setGroup(event.target.value);
            }}
          />
          <datalist id="component-groups">
            {groups.map((item) => (
              <option key={item} value={item} />
            ))}
          </datalist>
        </Field>
      )}

      {editing && kind === 'collection' && purpose !== 'users' && (
        <Field
          label="Раздел"
          hint="Формы живут отдельно от данных сайта: там заявки, отметки «новая» и подсказка по API."
        >
          <Segmented
            value={purpose}
            onValueChange={(next) => {
              setPurpose(next as TypeBody['purpose']);
            }}
            aria-label="Раздел админки"
            options={[
              { value: 'content', label: 'Модели — данные сайта' },
              { value: 'form', label: 'Формы — заявки с сайта' },
            ]}
          />
        </Field>
      )}

      {editing && isModel && !isForm && (
        <Field
          label="Адрес записи на сайте"
          hint={
            kind === 'single'
              ? 'Где эта страница на сайте, например «/about». Нужен живому предпросмотру.'
              : 'Шаблон: «/cases/{slug}» — {slug} заменится адресом записи. Нужен живому предпросмотру.'
          }
        >
          <Input
            spellCheck={false}
            value={route}
            placeholder={kind === 'single' ? '/about' : '/cases/{slug}'}
            onChange={(event) => {
              setRoute(event.target.value);
            }}
          />
        </Field>
      )}

      <Field
        label="Пояснение"
        hint={
          section === 'component'
            ? 'Где и зачем ставить этот компонент.'
            : isForm
              ? 'Что это за форма и где она стоит на сайте — видно команде.'
              : 'Видно тем, кто заполняет записи.'
        }
      >
        <Textarea
          rows={2}
          value={description}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />
      </Field>

      {editing && kind === 'collection' && !isForm && (
        <Switch checked={manualOrder} onCheckedChange={setManualOrder}>
          Ручной порядок — записи расставляют перетаскиванием на вкладке «Записи», сайт получает их
          в нём
        </Switch>
      )}

      {editing && isModel && purpose !== 'users' && (
        <Field
          label="Доступ с сайта"
          hint={
            isForm
              ? 'Создавать: всем — форма принимает заявки, никому — форма на паузе. Читать обычно никому: заявки видны только в админке.'
              : 'Что сайт может делать с записями через API. «Вошедшим» и «Только своё» — для посетителей, которые вошли на сайте (раздел «Пользователи»): заказ создаёт покупатель и видит только свои.'
          }
        >
          <div className={styles['access']}>
            <span className={styles['access__label']}>Читать</span>
            <Segmented
              value={readAccess}
              onValueChange={(next) => {
                setReadAccess(next as typeof readAccess);
              }}
              aria-label="Кто читает записи с сайта"
              options={[
                { value: 'public', label: 'Всем' },
                { value: 'signed_in', label: 'Вошедшим' },
                ...(kind === 'collection' ? [{ value: 'own', label: 'Только своё' }] : []),
                { value: 'none', label: 'Никому' },
              ]}
            />
            {kind === 'collection' && (
              <>
                <span className={styles['access__label']}>Создавать</span>
                <Segmented
                  value={createAccess}
                  onValueChange={(next) => {
                    setCreateAccess(next as typeof createAccess);
                  }}
                  aria-label="Кто создаёт записи с сайта"
                  options={[
                    { value: 'none', label: 'Никому' },
                    { value: 'public', label: 'Всем' },
                    { value: 'signed_in', label: 'Вошедшим' },
                  ]}
                />
                <span className={styles['access__label']}>Менять</span>
                <Segmented
                  value={updateAccess}
                  onValueChange={(next) => {
                    setUpdateAccess(next as typeof updateAccess);
                  }}
                  aria-label="Кто меняет записи с сайта"
                  options={[
                    { value: 'none', label: 'Никому' },
                    { value: 'own', label: 'Только своё' },
                  ]}
                />
                <span className={styles['access__label']}>Удалять</span>
                <Segmented
                  value={deleteAccess}
                  onValueChange={(next) => {
                    setDeleteAccess(next as typeof deleteAccess);
                  }}
                  aria-label="Кто удаляет записи с сайта"
                  options={[
                    { value: 'none', label: 'Никому' },
                    { value: 'own', label: 'Только своё' },
                  ]}
                />
              </>
            )}
          </div>
        </Field>
      )}

      {isModel && !isForm && (
        <Switch checked={seo} onCheckedChange={setSeo}>
          У записей есть SEO — заголовок и описание для поисковиков, картинка для соцсетей
        </Switch>
      )}

      <div className={styles['actions']}>
        <Button type="submit" loading={busy}>
          {editing
            ? 'Сохранить'
            : section === 'component'
              ? 'Завести компонент'
              : isForm
                ? 'Завести форму'
                : 'Завести модель'}
        </Button>
      </div>
    </form>
  );
}
